/**
 * SmartImportModal — Modal générique d'import Excel intelligent 3 étapes.
 * Fonctionne pour Contrats / Clients / Fournisseurs via prop `kind`.
 *   1. Upload  → POST /{kind}/import/preview (mapping AI Gemini)
 *   2. Edit    → utilisateur vérifie/ajuste + preview 3 lignes
 *   3. Confirm → POST /{kind}/import/confirm (insert batch atomique)
 */
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileSpreadsheet, CheckCircle, AlertCircle, ArrowRight,
  ArrowLeft, Sparkles, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STEP_UPLOAD = 1, STEP_MAP = 2, STEP_RESULT = 3;

const RESOURCE_LABELS = {
  contracts: { plural: "contrats", singular: "contrat", primaryField: "societe", primaryLabel: "Société / Client" },
  clients: { plural: "clients", singular: "client", primaryField: "name", primaryLabel: "Nom" },
  suppliers: { plural: "fournisseurs", singular: "fournisseur", primaryField: "name", primaryLabel: "Nom" },
  products: { plural: "produits", singular: "produit", primaryField: "name", primaryLabel: "Nom" },
};

export default function SmartImportModal({
  isOpen, onClose, onSuccess, kind = "contracts",
}) {
  const meta = RESOURCE_LABELS[kind] || RESOURCE_LABELS.contracts;
  const [step, setStep] = useState(STEP_UPLOAD);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep(STEP_UPLOAD); setFile(null); setBusy(false); setProgress(0);
    setPreviewData(null); setMapping({}); setResult(null);
  };
  const handleClose = () => { reset(); onClose(); };

  const handleFileChange = (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (!f.name.match(/\.(xlsx|xls)$/i)) { toast.error("Fichier Excel (.xlsx/.xls) requis"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 5 Mo)"); return; }
    setFile(f);
  };

  const handlePreview = async () => {
    if (!file) return;
    setBusy(true); setProgress(15);
    try {
      const fd = new FormData(); fd.append("file", file);
      setProgress(35);
      const r = await api.post(`/${kind}/import/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => setProgress(35 + Math.round((evt.loaded * 45) / (evt.total || 1))),
        timeout: 60000,
      });
      setProgress(100);
      setPreviewData(r.data);
      setMapping(r.data.detected_mapping || {});
      setStep(STEP_MAP);
      if (r.data.ai_powered) toast.success("Mapping IA détecté — vérifiez et validez");
      else toast.info("Mapping détecté par alias — vérifiez les colonnes");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); setProgress(0); }
  };

  const handleConfirm = async () => {
    if (!mapping[meta.primaryField]) { toast.error(`Le champ '${meta.primaryLabel}' doit être mappé`); return; }
    setBusy(true); setProgress(20);
    try {
      const r = await api.post(`/${kind}/import/confirm`,
        { import_id: previewData.import_id, mapping },
        { timeout: 120000 });
      setProgress(100);
      setResult(r.data);
      setStep(STEP_RESULT);
      toast.success(`${r.data.added} ${meta.singular}(s) importé(s)`);
      onSuccess?.(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); setProgress(0); }
  };

  const setFieldMapping = (canonical, header) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!header || header === "__none__") delete next[canonical];
      else next[canonical] = header;
      return next;
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto"
                      data-testid={`smart-import-modal-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            Import intelligent — {meta.plural}
            {previewData?.ai_powered && (
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 ml-2">
                <Sparkles className="w-3 h-3 mr-1" /> IA Gemini
              </Badge>
            )}
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mt-2 text-xs">
          {["Fichier", "Mapping", "Résultat"].map((label, i) => {
            const idx = i + 1;
            const active = step === idx, done = step > idx;
            return (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center font-medium
                  ${active ? "bg-blue-600 text-white" : done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                  {done ? <CheckCircle className="w-3.5 h-3.5" /> : idx}
                </div>
                <span className={active ? "font-semibold text-blue-700" : done ? "text-emerald-600" : "text-slate-500"}>
                  {label}
                </span>
                {idx < 3 && <ArrowRight className="w-3 h-3 text-slate-300 mx-1" />}
              </div>
            );
          })}
        </div>

        {step === STEP_UPLOAD && (
          <div className="space-y-4 mt-4">
            {kind === "products" && (
              <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-3 text-xs text-slate-700 leading-relaxed"
                   data-testid="products-import-explainer">
                <div className="font-semibold text-blue-900 mb-1 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                  À quoi sert l'import Excel ?
                </div>
                Utile pour <b>alimenter ton catalogue en une fois</b> à partir d'un fichier existant (export
                Axonaut/Pennylane/Sage, tableur maison, liste tarifaire fournisseur…). Colonnes attendues :
                <span className="font-mono text-[11px] text-blue-800"> Nom, Description, Prix HT, TVA, Unité, Référence, Catégorie</span>.
                Notre IA détecte automatiquement les colonnes de ton fichier, tu valides le mapping en 1 clic,
                les doublons (même nom) sont ignorés.
              </div>
            )}
            <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${file ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400"}`}>
              <input type="file" accept=".xlsx,.xls" onChange={handleFileChange}
                className="hidden" id={`smart-import-file-${kind}`} name={`smart-import-file-${kind}`} disabled={busy} />
              <label htmlFor={`smart-import-file-${kind}`} className="cursor-pointer">
                {file ? (
                  <div className="space-y-2">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-blue-600" />
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(1)} Ko</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 mx-auto text-slate-400" />
                    <p className="font-medium">Cliquez ou déposez votre fichier Excel</p>
                    <p className="text-sm text-slate-500">
                      Fonctionne avec Axonaut, Pennylane, Sage, Excel maison…
                    </p>
                    <p className="text-xs text-slate-400">Max 5 Mo · 5000 lignes</p>
                  </div>
                )}
              </label>
            </div>
            {busy && (
              <div className="space-y-2">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-center text-slate-500 flex items-center justify-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                  Analyse IA du fichier... {progress}%
                </p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose} disabled={busy}>Annuler</Button>
              <Button onClick={handlePreview} disabled={!file || busy}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="smart-analyze-btn">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Analyser le fichier
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === STEP_MAP && previewData && (
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 text-blue-900 font-medium">
                <CheckCircle className="w-4 h-4" />
                {previewData.row_count} ligne(s) détectée(s) dans <span className="font-mono">{previewData.filename}</span>
              </div>
              <p className="text-blue-700 text-xs mt-1">
                {previewData.ai_powered
                  ? "Notre IA a proposé le mapping ci-dessous. Corrigez si besoin puis validez."
                  : "Mapping détecté par alias. Vérifiez chaque champ puis validez."}
              </p>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 grid grid-cols-2 gap-2 text-xs font-medium text-slate-500 uppercase">
                <div>Champ cible</div>
                <div>Colonne de votre fichier</div>
              </div>
              <div className="max-h-[300px] overflow-y-auto divide-y">
                {Object.entries(previewData.canonical_fields).map(([canonical, desc]) => {
                  const isRequired = canonical === meta.primaryField;
                  const current = mapping[canonical] || "__none__";
                  return (
                    <div key={canonical} className="grid grid-cols-2 gap-2 px-3 py-2 items-center hover:bg-slate-50"
                         data-testid={`mapping-row-${canonical}`}>
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {canonical} {isRequired && <span className="text-red-500">*</span>}
                        </div>
                        <div className="text-xs text-slate-500">{desc}</div>
                      </div>
                      <Select value={current} onValueChange={(v) => setFieldMapping(canonical, v)}>
                        <SelectTrigger className="h-9" data-testid={`mapping-select-${canonical}`}>
                          <SelectValue placeholder="— Non mappé —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__"><span className="text-slate-400 italic">— Non mappé —</span></SelectItem>
                          {previewData.headers.map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>

            {previewData.preview && previewData.preview.length > 0 && (
              <div>
                <Label className="text-sm text-slate-700 mb-2 block">
                  Aperçu des 3 premières lignes ({Object.keys(mapping).length} champs mappés)
                </Label>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        {Object.keys(mapping).slice(0, 6).map((k) => (
                          <th key={k} className="px-2 py-2 text-left font-medium text-slate-500 whitespace-nowrap">{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.preview.map((line, i) => (
                        <tr key={i} className="border-t" data-testid={`preview-row-${i}`}>
                          {Object.keys(mapping).slice(0, 6).map((k) => (
                            <td key={k} className="px-2 py-2 text-slate-700 whitespace-nowrap">
                              {line[k] != null ? String(line[k]).slice(0, 40) : <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {busy && (
              <div className="space-y-2">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-center text-slate-500">Import en cours... {progress}%</p>
              </div>
            )}

            <div className="flex justify-between gap-3">
              <Button variant="outline" onClick={() => { setStep(STEP_UPLOAD); setPreviewData(null); }} disabled={busy}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
              </Button>
              <Button onClick={handleConfirm} disabled={busy || !mapping[meta.primaryField]}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="smart-confirm-btn">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Importer {previewData.row_count} {meta.singular}(s)
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === STEP_RESULT && result && (
          <div className="space-y-4 mt-4">
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-3" />
              <p className="text-xl font-bold text-slate-900">Import terminé</p>
              <p className="text-slate-500 mt-1">{result.filename}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="border rounded-lg p-3 text-center bg-emerald-50 border-emerald-200">
                <p className="text-2xl font-bold text-emerald-700 font-mono">{result.added}</p>
                <p className="text-xs text-emerald-600 uppercase mt-1">Importés</p>
              </div>
              <div className="border rounded-lg p-3 text-center bg-slate-50 border-slate-200">
                <p className="text-2xl font-bold text-slate-700 font-mono">{result.skipped}</p>
                <p className="text-xs text-slate-500 uppercase mt-1">Ignorés</p>
              </div>
              <div className={`border rounded-lg p-3 text-center
                ${result.errors?.length ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
                <p className={`text-2xl font-bold font-mono ${result.errors?.length ? "text-amber-700" : "text-slate-500"}`}>
                  {result.errors?.length || 0}
                </p>
                <p className={`text-xs uppercase mt-1 ${result.errors?.length ? "text-amber-600" : "text-slate-500"}`}>Erreurs</p>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="border rounded-lg p-3 bg-amber-50 border-amber-200 max-h-[150px] overflow-y-auto">
                <p className="text-xs font-medium text-amber-800 uppercase mb-1">Détail des lignes ignorées</p>
                {result.errors.map((e, i) => (
                  <div key={i} className="text-xs text-amber-900">
                    <span className="font-mono">Ligne {e.row}</span> : {e.reason}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={handleClose} className="bg-blue-600 hover:bg-blue-700 text-white">
                Fermer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
